/**
 * Shared cache tag registry — single source of truth for tag metadata.
 *
 * cacheStore routing is implemented in frontend-next/cache-handler.mjs setStrategy.
 * revalidate values can be overridden via NEXT_PUBLIC_* env vars in frontend-next;
 * defaultRevalidate reflects the compile-time defaults.
 */

/**
 * Which Redis handler a tag's entries are stored in.
 *
 * "redis-xi-assets" — keyPrefix `mw-nextjs:xi-assets:` — NOT version-keyed.
 *   Persists across deploys; expires only by TTL or explicit revalidateTag().
 *
 * "redis-builds"    — keyPrefix `mw-nextjs:${appVersion}:` — IS version-keyed.
 *   Invalidated automatically when APP_VERSION changes on a new deploy.
 */
export type CacheStore = "redis-xi-assets" | "redis-builds";

export interface CacheTagMeta {
  tag: string;
  label: string;
  description: string;
  cacheStore: CacheStore;
  /** Whether cache entries survive a production deploy (new APP_VERSION). */
  survivesBuilds: boolean;
  /** Default TTL in seconds. May be overridden by NEXT_PUBLIC_* env vars in frontend-next. */
  defaultRevalidate: number;
}

export const CACHE_TAG_REGISTRY_BASE = [
  {
    tag: "xi-assets-cache",
    label: "All XI Assets",
    description: "Broad invalidation — clears all XI API cached data (sounds + generations).",
    cacheStore: "redis-xi-assets" as CacheStore,
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-shared-sounds",
    label: "XI Shared Sounds",
    description: "Shared sound generation results on SFX category pages.",
    cacheStore: "redis-xi-assets" as CacheStore,
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-music",
    label: "XI Music",
    description: "Shared songs from the marketplace.",
    cacheStore: "redis-xi-assets" as CacheStore,
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-shared-generations",
    label: "XI Shared Generations",
    description: "Shared image/video generation results on external model pages.",
    cacheStore: "redis-xi-assets" as CacheStore,
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "xi-iconic-voices",
    label: "XI Iconic Voices",
    description: "Iconic voice metadata from the ElevenLabs API (slider + table blocks).",
    cacheStore: "redis-xi-assets" as CacheStore,
    survivesBuilds: true,
    defaultRevalidate: 7 * 24 * 60 * 60, // 7 days (NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD)
  },
  {
    tag: "ashby-jobs",
    label: "Ashby Job Listings",
    description: "Job postings and locations fetched from the Ashby API (/careers).",
    cacheStore: "redis-builds" as CacheStore,
    survivesBuilds: false,
    defaultRevalidate: 10 * 60, // 10 min (NEXT_PUBLIC_ASHBY_CACHE_PERIOD)
  },
  {
    tag: "music-song",
    label: "Music Songs",
    description: "Shared music song data on music pages.",
    cacheStore: "redis-builds" as CacheStore,
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "soundboard-sounds",
    label: "Soundboard Sounds",
    description: "CMS sound effects fetched for soundboard pages.",
    cacheStore: "redis-builds" as CacheStore,
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "find-slug",
    label: "Find Slug",
    description: "Slug→collection lookup results used during page rendering.",
    cacheStore: "redis-builds" as CacheStore,
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "posthog-feature-flags",
    label: "PostHog Feature Flags",
    description: "Server-side PostHog feature flag decisions.",
    cacheStore: "redis-builds" as CacheStore,
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_CACHE_PERIOD)
  },
  {
    tag: "product-ratings",
    label: "Product Ratings",
    description: "Apple App Store rating fetched from the iTunes Search API for SoftwareApp structured data.",
    cacheStore: "redis-xi-assets" as CacheStore,
    survivesBuilds: true,
    defaultRevalidate: 24 * 60 * 60, // 24h
  },
  {
    tag: "markdown-cache",
    label: "Markdown variant cache",
    description: "Broad invalidation — clears all converted Markdown pages served via /next/markdown.",
    cacheStore: "redis-builds" as CacheStore,
    survivesBuilds: false,
    defaultRevalidate: 5 * 60, // 5 min (NEXT_PUBLIC_PAYLOAD_CACHE_PERIOD)
  },
] as const satisfies CacheTagMeta[];

export type KnownCacheTag = (typeof CACHE_TAG_REGISTRY_BASE)[number]["tag"];

export const KNOWN_CACHE_TAGS = CACHE_TAG_REGISTRY_BASE.map(e => e.tag) as KnownCacheTag[];

// ─── Cache registry API types ────────────────────────────────────────────────
// Shared between frontend-next (cache-registry route) and cms-payload (CacheManager views).

export interface CacheRegistryGlobals {
  defaultCachePeriod: number;
  payloadCachePeriod: number;
  xiAssetsCachePeriod: number;
}

export interface AssetTranslationCacheMeta {
  label: string;
  description: string;
  ttlSeconds: number;
  keyPrefix: string;
}

export interface CacheRegistryResponse {
  tags: (CacheTagMeta & { revalidate: number })[];
  globals: CacheRegistryGlobals;
  assetTranslationCache: AssetTranslationCacheMeta;
}
